/**
 * GanttView Component - Main Gantt chart view for tracker
 * Filters issues with dates and builds hierarchical task structure
 */

import { useMemo, useState } from 'react';
import type { Issue } from '../../../api/work';
import {
  GanttChart,
  buildHierarchicalTasks,
  type ViewMode,
  type GanttTask,
} from '../gantt/GanttChart';

interface GanttViewProps {
  issues: Record<string, Issue>;
  onIssueClick?: (issueId: string) => void;
  onUpdateIssue?: (issueId: string, updates: Partial<Issue>) => Promise<void>;
}

export function GanttView({ issues, onIssueClick, onUpdateIssue }: GanttViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('Week');

  // Filter issues to only those with dates
  const issuesWithDates = useMemo(() => {
    return Object.values(issues).filter(
      (issue) => issue.start_date || issue.due_date
    );
  }, [issues]);

  // Build hierarchical task structure
  const ganttTasks = useMemo(() => {
    if (issuesWithDates.length === 0) return [];
    return buildHierarchicalTasks(issuesWithDates);
  }, [issuesWithDates]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = issuesWithDates.length;
    const byType = issuesWithDates.reduce(
      (acc, issue) => {
        acc[issue.type] = (acc[issue.type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const completed = issuesWithDates.filter(
      (issue) => issue.status === 'done'
    ).length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      total,
      byType,
      completed,
      completionRate,
    };
  }, [issuesWithDates]);

  const handleTaskClick = (task: GanttTask) => {
    if (onIssueClick) {
      onIssueClick(task.id);
    }
  };

  const handleDateChange = async (task: GanttTask, startDate: Date, endDate: Date) => {
    if (onUpdateIssue) {
      const start_date = startDate.toISOString().split('T')[0];
      const due_date = endDate.toISOString().split('T')[0];
      await onUpdateIssue(task.id, { start_date, due_date });
    }
  };

  const handleProgressChange = async (task: GanttTask, progress: number) => {
    if (onUpdateIssue) {
      // Map progress to status: 0% = backlog, 50% = in_progress, 100% = done
      let status: string;
      if (progress === 0) {
        status = 'backlog';
      } else if (progress === 100) {
        status = 'done';
      } else {
        status = 'in_progress';
      }
      await onUpdateIssue(task.id, { status });
    }
  };

  return (
    <div className="gantt-view h-full flex flex-col">
      {/* Stats Header */}
      <div className="px-6 py-4 border-b border-tracker-border bg-tracker-bg-subtle flex justify-between items-center">
        <div className="flex gap-8 items-center">
          <div>
            <div className="text-xs text-tracker-text-light mb-1">
              Total Tasks
            </div>
            <div className="text-2xl font-bold text-tracker-text">
              {stats.total}
            </div>
          </div>
          <div>
            <div className="text-xs text-tracker-text-light mb-1">
              Completion Rate
            </div>
            <div className="text-2xl font-bold text-tracker-status-success">
              {stats.completionRate}%
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            {stats.byType.epic && (
              <div className="text-tracker-text-muted">
                <span className="font-semibold text-tracker-type-epic">{stats.byType.epic}</span> Epics
              </div>
            )}
            {stats.byType.story && (
              <div className="text-tracker-text-muted">
                <span className="font-semibold text-tracker-type-story">{stats.byType.story}</span> Stories
              </div>
            )}
            {stats.byType.task && (
              <div className="text-tracker-text-muted">
                <span className="font-semibold text-tracker-type-task">{stats.byType.task}</span> Tasks
              </div>
            )}
            {stats.byType.bug && (
              <div className="text-tracker-text-muted">
                <span className="font-semibold text-tracker-type-bug">{stats.byType.bug}</span> Bugs
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-tracker-type-epic rounded-sm" />
            <span className="text-tracker-text-muted">Epic</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-tracker-type-story rounded-sm" />
            <span className="text-tracker-text-muted">Story</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-tracker-type-task rounded-sm" />
            <span className="text-tracker-text-muted">Task</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-tracker-type-bug rounded-sm" />
            <span className="text-tracker-text-muted">Bug</span>
          </div>
        </div>
      </div>

      {/* Warning if no tasks with dates */}
      {issuesWithDates.length === 0 && (
        <div className="p-8 text-center bg-coral-50 border-b border-tracker-border">
          <div className="text-3xl mb-2">📅</div>
          <div className="text-sm text-tracker-text-muted mb-1">
            No tasks with dates found
          </div>
          <div className="text-xs text-tracker-text-light">
            Add start_date or due_date to issues to display them in the Gantt chart
          </div>
        </div>
      )}

      {/* Info Banner */}
      {issuesWithDates.length > 0 && Object.values(issues).length > issuesWithDates.length && (
        <div className="px-6 py-3 bg-tracker-gold-100 border-b border-tracker-border text-sm text-tracker-gold-800 flex items-center gap-2">
          <svg
            className="w-4 h-4 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            Showing {issuesWithDates.length} of {Object.values(issues).length} issues (issues
            without dates are hidden)
          </span>
        </div>
      )}

      {/* Gantt Chart */}
      <div className="flex-1 overflow-hidden">
        <GanttChart
          tasks={ganttTasks}
          onTaskClick={handleTaskClick}
          onDateChange={handleDateChange}
          onProgressChange={handleProgressChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      </div>
    </div>
  );
}
